// 掃討の短い演出（約3秒）：ちびキャラが戦場を駆け抜けて敵を次々に倒し、最後に敵本陣を落とす → 呼び出し側が周回の成果（haul-fx）へ。
// 戦闘画面は使わない（絵だけの小芝居）。押すとすぐ終わる。prefers-reduced-motion なら出さない。
import type { CharId } from '@hyakki/core';
import { WORLD, enemyIconHtml, spriteStyle } from '../assets';
import { sfx, vary } from '../audio';
import { fmt } from './dom';
import '../styles/screens/sweep-fx.css';

/** 寸劇のキャラ：横長の元絵（heroes の 768×512 のコマ）。正方形のちび絵は武器などの左右が切れているため */
const HERO_CELL: Record<string, [number, number]> = { akari: [0, 0], kazamaru: [1, 0], suzune: [0, 1], yuzuha: [1, 1] };
const heroStyle = (id: CharId) => {
  const [col, row] = HERO_CELL[id] ?? [0, 0];
  return spriteStyle({ sheet: 'heroes', col, row });
};
const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const reduced = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * 掃討の結果を待つあいだ、戦場を次々に落としていく。done が決まったら、その回の決着のあとに判子を出して終わる。
 * done が null で決まったら（失敗）、すぐ閉じる。progress は今までに済んだ戦数（右上に出す）
 */
export function playSweepFx(o: { charId: CharId; enemyIds: string[]; terrain: string; done: Promise<number | null>; progress?: () => number }): Promise<void> {
  if (reduced()) return o.done.then(() => undefined);
  return new Promise((resolve) => {
    const foes = [...new Set(o.enemyIds)].slice(0, 3);
    while (foes.length && foes.length < 3) foes.push(foes[foes.length - 1]);
    const el = document.createElement('div');
    el.className = 'swfx';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = `<div class="swfx-bg" style="background-image:url(/art/battlefield/${o.terrain}.webp)"></div>
      <div class="swfx-band"></div>
      <p class="swfx-count"><b>0</b><small>戦</small></p>
      <p class="swfx-stamp"><b>掃討</b><small></small></p>`;
    // 枠（暗い幕）はすぐ出し、中身は背景の絵を読んでから見せる（先に見せると、キャラだけが浮いて、あとから背景がぱっと出る）。読めなくても 0.8秒で見せる
    el.classList.add('loading');
    (document.getElementById('stage') ?? document.body).appendChild(el);
    const shown = new Promise<void>((r) => {
      const img = new Image();
      img.onload = img.onerror = () => r();
      img.src = `/art/battlefield/${o.terrain}.webp`;
      setTimeout(r, 800);
    }).then(() => el.classList.remove('loading'));
    const band = el.querySelector<HTMLElement>('.swfx-band')!;
    const countEl = el.querySelector<HTMLElement>('.swfx-count b')!;
    let final: number | null | undefined;
    void o.done.then((n) => (final = n), () => (final = null));
    let closed = false;
    const end = () => {
      if (closed) return;
      closed = true;
      resolve();
      // 次の画面（周回の成果）が上に出そろうまで下に残す（すぐ消すと、そのすき間に前の画面が透けてちらつく）。失敗で閉じるときは薄くして消す
      if (final == null) el.classList.add('out');
      setTimeout(() => el.remove(), final == null ? 250 : 600);
    };
    // 決まったあとは押すとすぐ閉じる
    el.addEventListener('click', () => final !== undefined && end());
    const tick = () => (countEl.textContent = fmt(final ?? o.progress?.() ?? 0));
    /** 1つの戦場：左から駆け寄って3体を斬り、敵本陣を落とす */
    const round = async () => {
      el.classList.remove('fall');
      band.innerHTML = `<span class="swfx-core" style="${spriteStyle(WORLD.coreRed)}"></span>
        ${foes.map((id, i) => `<span class="swfx-foe" style="--x:${30 + i * 18}%">${enemyIconHtml(id, 64)}</span>`).join('')}
        <span class="swfx-hero"><span class="swfx-hero-i" style="${heroStyle(o.charId)}"></span></span>
        <span class="swfx-smoke"></span>`;
      const hero = band.querySelector<HTMLElement>('.swfx-hero')!;
      await wait(200);
      sfx('move', 0.5);
      for (const f of band.querySelectorAll<HTMLElement>('.swfx-foe')) {
        if (closed) return;
        hero.style.setProperty('--hx', `calc(${f.style.getPropertyValue('--x')} - 9%)`);
        await wait(210);
        hero.classList.remove('slash');
        void hero.offsetWidth;
        hero.classList.add('slash');
        f.classList.add('down');
        sfx('hit', 0.6, vary());
        await wait(220);
      }
      if (closed) return;
      hero.style.setProperty('--hx', '70%');
      await wait(240);
      hero.classList.remove('slash');
      void hero.offsetWidth;
      hero.classList.add('slash');
      sfx('hitHeavy', 0.7);
      el.classList.add('fall');
      await wait(160);
      sfx('coreDown', 0.6);
      tick();
      await wait(700);
    };
    void (async () => {
      await shown;
      // 結果が来るまで戦場を落とし続ける（来たら、その回の決着まで見せる）
      do {
        await round();
        tick();
      } while (!closed && final === undefined);
      if (closed) return;
      if (final == null) return end();
      el.querySelector('.swfx-stamp small')!.textContent = `${fmt(final)}戦`;
      el.classList.add('stamp');
      sfx('omikujiStamp', 0.8);
      await wait(1000);
      end();
    })();
  });
}

/**
 * 節電モードの寸劇：枠の中に戦場の帯を置き、手番が進むたびに短く（約2秒）1体を斬る。決着で本陣が崩れる。
 * 動くのはその間だけ（残りは止まっている）ので電池をほとんど使わない
 */
export function mountSkit(host: HTMLElement, o: { charId: CharId; enemyIds: string[]; terrain: string }) {
  const foes = [...new Set(o.enemyIds)].slice(0, 3);
  while (foes.length && foes.length < 3) foes.push(foes[foes.length - 1]);
  const el = document.createElement('div');
  el.className = 'swfx skit';
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = `<div class="swfx-bg" style="background-image:url(/art/battlefield/${o.terrain}.webp)"></div><div class="swfx-band"></div>`;
  host.prepend(el);
  const band = el.querySelector<HTMLElement>('.swfx-band')!;
  let next = 0;
  let busy = false;
  const reset = () => {
    next = 0;
    el.classList.remove('fall');
    band.innerHTML = `<span class="swfx-core" style="${spriteStyle(WORLD.coreRed)}"></span>
      ${foes.map((id, i) => `<span class="swfx-foe" style="--x:${30 + i * 18}%">${enemyIconHtml(id, 64)}</span>`).join('')}
      <span class="swfx-hero"><span class="swfx-hero-i" style="${heroStyle(o.charId)}"></span></span>`;
  };
  reset();
  const hero = () => band.querySelector<HTMLElement>('.swfx-hero')!;
  const strike = async (x: string, target: HTMLElement | null) => {
    const h = hero();
    h.classList.add('go');
    h.style.setProperty('--hx', x);
    await wait(260);
    h.classList.remove('slash');
    void h.offsetWidth;
    h.classList.add('slash');
    target?.classList.add('down');
    await wait(500);
    h.classList.remove('go');
  };
  return {
    /** 手番が進んだ：次の1体を斬る（3体斬ったら新しい戦場に） */
    async turn() {
      if (busy || !el.isConnected) return;
      busy = true;
      const foesEl = [...band.querySelectorAll<HTMLElement>('.swfx-foe')];
      if (next >= foesEl.length) reset();
      const f = [...band.querySelectorAll<HTMLElement>('.swfx-foe')][next++];
      await strike(`calc(${f.style.getPropertyValue('--x')} - 9%)`, f);
      busy = false;
    },
    /** 決着（勝ち）：本陣を落として、次の戦場へ */
    async win() {
      if (!el.isConnected) return;
      busy = true;
      await strike('70%', null);
      el.classList.add('fall');
      await wait(1300);
      reset();
      busy = false;
    },
    destroy: () => el.remove(),
  };
}
